import { useEffect, useState } from 'react';
import { useTenantConfig } from './useTenantConfig';
import { useAuth } from './useAuth';
import { investigationsApi } from '../services/investigationsApi';

// Concurrent consumers share one check for the current authenticated profile.
// Profiles are replaced at login; logout also clears checks for the prior session.
let checks = new WeakMap<object, Promise<boolean>>();

/** Whether this user may open the Agent (investigations) page. */
export function useInvestigationsAccess(): boolean {
  const { features } = useTenantConfig();
  const { user, isAuthenticated, isLoading } = useAuth();
  const enabled = Boolean(features.agent_investigations);
  const [access, setAccess] = useState<{ user: object; allowed: boolean } | null>(null);
  useEffect(() => {
    if (!isAuthenticated) {
      checks = new WeakMap();
      setAccess(null);
      return;
    }
    if (!enabled || isLoading || !user) {
      setAccess(null);
      return;
    }
    let alive = true;
    const sessionChecks = checks;
    let check = sessionChecks.get(user);
    if (!check) {
      // Keep the same result for every consumer of this profile, including a
      // failed check. A new authenticated profile/session gets a fresh check.
      check = investigationsApi.configuration().then(() => true, () => false);
      sessionChecks.set(user, check);
    }
    void check.then(allowed => { if (alive) setAccess({ user, allowed }); });
    return () => { alive = false; };
  }, [enabled, isAuthenticated, isLoading, user]);
  return Boolean(enabled && !isLoading && isAuthenticated && access?.user === user && access?.allowed);
}
